import React, { useCallback, useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { useDropzone } from 'react-dropzone'
import { CloudUploadIcon, XIcon } from '@heroicons/react/solid'

import { useGlobalState } from '@/state'
import useQuery from '@/hooks/useQuery'

const MAX_SIZE = 8 * 1024 * 1024

const sanitizeBaseName = (name) => {
  const base = name.replace(/\.[^/.]+$/, '').replace(/[^\w\d_\-.]+/ig, '').slice(0, 40)
  return base || 'image'
}

const FeedbackImageUploader = ({ urls, onChange, max = 5 }) => {
  const [, setToast] = useGlobalState('toast')
  const [currentUser] = useGlobalState('currentUser')
  const { getRequest } = useQuery()
  const [pending, setPending] = useState([])
  const isMounted = useRef(false)

  useEffect(() => {
    isMounted.current = true
    return () => { isMounted.current = false }
  }, [])

  const remainingSlots = Math.max(0, max - urls.length - pending.length)

  const uploadOne = (file, pendingId) => {
    const ext = (file.name.split('.').pop() || 'png').toLowerCase()
    const rand = Math.random().toString(36).slice(2, 8)
    const key = `feedback-${currentUser.id}-${Date.now()}-${rand}-${sanitizeBaseName(file.name)}.${ext}`

    getRequest('/api/v1/image_uploads/r2_hash', { key, kind: 'Feedback', content_type: file.type }, (err, jsonData) => {
      if (err || !jsonData?.url) {
        if (isMounted.current) setPending(p => p.filter(x => x.id !== pendingId))
        setToast(<div className='ml-3 w-0 flex-1 pt-0.5'>
          <p className='text-sm font-medium text-red-600'>Upload error</p>
          <p className='mt-1 text-sm text-red-400'>{err?.errors || err || 'Could not get an upload URL.'}</p>
        </div>)
        return
      }

      window.fetch(jsonData.url, {
        method: 'PUT',
        headers: { 'Content-Type': file.type },
        body: file
      }).then((response) => {
        if (!response.ok) {
          if (isMounted.current) setPending(p => p.filter(x => x.id !== pendingId))
          setToast(<div className='ml-3 w-0 flex-1 pt-0.5'>
            <p className='text-sm font-medium text-red-600'>Upload error</p>
            <p className='mt-1 text-sm text-red-400'>R2 returned {response.status}.</p>
          </div>)
          return
        }
        const finalUrl = jsonData.image_url + key
        if (isMounted.current) setPending(p => p.filter(x => x.id !== pendingId))
        onChange([...urls, finalUrl])
      }).catch(() => {
        if (isMounted.current) setPending(p => p.filter(x => x.id !== pendingId))
        setToast(<div className='ml-3 w-0 flex-1 pt-0.5'>
          <p className='text-sm font-medium text-red-600'>Upload error</p>
          <p className='mt-1 text-sm text-red-400'>Network error talking to R2.</p>
        </div>)
      })
    })
  }

  const onDrop = useCallback((acceptedFiles, rejectedFiles) => {
    if (rejectedFiles.length > 0) {
      setToast(<div className='ml-3 w-0 flex-1 pt-0.5'>
        <p className='text-sm font-medium text-red-600'>Upload error</p>
        <p className='mt-1 text-sm text-red-400'>jpg, png, gif, or webp only. Max 8MB.</p>
      </div>)
    }
    const slots = Math.max(0, max - urls.length - pending.length)
    const toUpload = acceptedFiles.slice(0, slots)
    if (acceptedFiles.length > slots) {
      setToast(<div className='ml-3 w-0 flex-1 pt-0.5'>
        <p className='text-sm font-medium text-red-600'>Too many images</p>
        <p className='mt-1 text-sm text-red-400'>Up to {max} total, kept the first {slots}.</p>
      </div>)
    }
    const newPending = toUpload.map(file => ({
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
      preview: URL.createObjectURL(file)
    }))
    setPending(p => [...p, ...newPending])
    toUpload.forEach((file, idx) => uploadOne(file, newPending[idx].id))
  }, [urls, pending, max])

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'image/jpeg': [], 'image/png': [], 'image/gif': [], 'image/webp': [] },
    maxSize: MAX_SIZE,
    multiple: true,
    disabled: remainingSlots === 0
  })

  const removeAt = (idx) => onChange(urls.filter((_, i) => i !== idx))

  return (
    <div>
      {(urls.length > 0 || pending.length > 0) && (
        <div className='mb-3 grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2'>
          {urls.map((url, idx) => (
            <div key={url} className='relative group aspect-w-1 aspect-h-1 overflow-hidden rounded-md border border-gray-200 dark:border-gray-700'>
              <img src={url} alt='' className='object-cover w-full h-full' />
              <button
                type='button'
                onClick={() => removeAt(idx)}
                className='absolute top-1 right-1 inline-flex items-center justify-center h-6 w-6 rounded-full bg-black/60 text-white hover:bg-black/80'
                aria-label='Remove image'
              >
                <XIcon className='h-3.5 w-3.5' />
              </button>
            </div>
          ))}
          {pending.map(p => (
            <div key={p.id} className='relative aspect-w-1 aspect-h-1 overflow-hidden rounded-md border border-gray-200 dark:border-gray-700'>
              <img src={p.preview} alt='' className='object-cover w-full h-full opacity-50' />
              <div className='absolute inset-0 flex items-center justify-center'>
                <span className='text-xs font-medium text-white bg-black/60 rounded px-2 py-1'>Uploading…</span>
              </div>
            </div>
          ))}
        </div>
      )}
      {remainingSlots > 0 && (
        <div
          {...getRootProps()}
          className={`relative block border-2 border-dashed rounded-lg p-6 text-center cursor-pointer ${isDragActive ? 'border-clcpurple bg-clcpurple/5' : 'border-gray-300 dark:border-gray-600 hover:border-gray-400'}`}
        >
          <input {...getInputProps()} />
          <CloudUploadIcon className='mx-auto h-8 w-8 text-gray-400' />
          <span className='mt-1 block text-sm font-medium text-gray-700 dark:text-gray-300'>
            Click or drop images ({remainingSlots} {remainingSlots === 1 ? 'slot' : 'slots'} left)
          </span>
          <span className='mt-1 block text-xs text-gray-500'>jpg, png, gif, webp · max 8MB each</span>
        </div>
      )}
    </div>
  )
}

FeedbackImageUploader.propTypes = {
  urls: PropTypes.arrayOf(PropTypes.string).isRequired,
  onChange: PropTypes.func.isRequired,
  max: PropTypes.number
}

export default FeedbackImageUploader
